import { z } from "zod";
/**
 * @payday/contracts — balance adjustment contract (single source of truth).
 *
 * This module is the ONE place the validation rules for balance adjustments
 * (overtime pot / holiday days) are defined, so that every surface
 * (OvertimePotDialog, BalanceResolutionModal, API domain conversion, API zod
 * schemas) agrees. Previously each surface re-implemented these rules and
 * they drifted apart (see handoff: reason min 8/15/1/15, missing 2-decimal
 * rounding in BalanceResolutionModal, negative-balance asymmetry).
 */
export const BALANCE_ADJUSTMENT_TYPES = [
    "overtime_hours",
    "holiday_days",
];
export const BALANCE_ADJUSTMENT_INPUT_UNITS = ["hours", "days"];
/**
 * Round to 2 decimal places using the same EPSILON convention the API used.
 */
export const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;
/**
 * Canonical rule: a resulting balance MAY go below zero. A negative overtime
 * pot or holiday balance is legitimate (e.g. reclaiming 4h from an 8h/4h
 * mistake). Any surface that clamps the result to >= 0 violates this contract.
 */
export const NEGATIVE_BALANCE_ALLOWED = true;
/**
 * Canonical audit-reason rule (settled: min 8, max 500, trimmed).
 */
export const BALANCE_ADJUSTMENT_REASON = {
    min: 8,
    max: 500,
};
/**
 * Accepts an amount string as typed by a user and returns the canonical
 * 2-decimal number, or null when the input is invalid.
 *
 * Mirrors OvertimePotDialog's intended behaviour:
 *   - trims surrounding whitespace
 *   - accepts a comma as a decimal separator (",", -> ".")
 *   - requires the canonical regex /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/
 *   - rounds to 2 decimals
 *   - rejects non-finite and zero (a zero change is meaningless and the
 *     backend would round it to 0 anyway)
 *
 * Returns the ROUNDED value (e.g. "1,5" -> 1.5, "-1.005" -> -1).
 */
export const normalizeBalanceAdjustmentAmount = (input) => {
    const normalized = input.trim().replace(",", ".");
    if (!BALANCE_ADJUSTMENT_AMOUNT_PATTERN.test(normalized))
        return null;
    const value = Number(normalized);
    if (!Number.isFinite(value))
        return null;
    const rounded = round2(value);
    if (!Number.isFinite(rounded) || rounded === 0)
        return null;
    return rounded;
};
export const BALANCE_ADJUSTMENT_AMOUNT_PATTERN = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/;
/**
 * Canonical audit-reason validity: trimmed length within [min, max].
 */
export const isBalanceAdjustmentReasonValid = (reason) => {
    const length = reason.trim().length;
    return (length >= BALANCE_ADJUSTMENT_REASON.min &&
        length <= BALANCE_ADJUSTMENT_REASON.max);
};
/**
 * Per-type unit policy: overtime must be entered in hours; holiday days may be
 * entered in hours or days.
 */
export const BALANCE_ADJUSTMENT_UNIT_POLICY = {
    overtime_hours: ["hours"],
    holiday_days: ["hours", "days"],
};
/* ------------------------------------------------------------------ *
 * Zod schemas (so surfaces can validate wire payloads with one rule)  *
 * ------------------------------------------------------------------ */
export const BalanceAdjustmentAmountSchema = z
    .string()
    .refine((value) => normalizeBalanceAdjustmentAmount(value) !== null, "Enter a nonzero change in hours with up to two decimal places.");
export const BalanceAdjustmentReasonSchema = z
    .string()
    .trim()
    .min(BALANCE_ADJUSTMENT_REASON.min)
    .max(BALANCE_ADJUSTMENT_REASON.max);
export const BalanceAdjustmentInputSchema = z
    .object({
    balanceType: z.enum(BALANCE_ADJUSTMENT_TYPES),
    inputAmount: BalanceAdjustmentAmountSchema,
    inputUnit: z.enum(BALANCE_ADJUSTMENT_INPUT_UNITS),
})
    .strict()
    .superRefine((value, context) => {
    const allowed = BALANCE_ADJUSTMENT_UNIT_POLICY[value.balanceType];
    if (!allowed.includes(value.inputUnit)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["inputUnit"],
            message: value.balanceType === "overtime_hours"
                ? "Overtime adjustments must be entered in hours."
                : "Unsupported balance adjustment unit.",
        });
    }
});
//# sourceMappingURL=balance-adjustment.js.map